import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlayArrow, Pause } from '@material-ui/icons';
import {
  Container,
  PlayerRow,
  PlayButton,
  WaveformContainer,
  Bar,
  Duration,
} from './styles';

interface AudioPlayerProps {
  url: string;
}

const BARS_COUNT = 25;

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + str.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash |= 0;
  }
  return Math.abs(hash) || 1;
};

const generateBars = (seedSource: string): number[] => {
  const seed = hashString(seedSource);
  const bars: number[] = [];
  let x = seed;
  for (let i = 0; i < BARS_COUNT; i += 1) {
    x = (x * 9301 + 49297) % 233280;
    const r = x / 233280;
    bars.push(0.25 + r * 0.75);
  }
  return bars;
};

const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({ url }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const bars = useMemo(() => generateBars(url), [url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {
        /* autoplay/network errors silently ignored */
      });
    } else {
      audio.pause();
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = Math.max(0, Math.min(duration, ratio * duration));
  };

  const progress = duration > 0 ? currentTime / duration : 0;
  const displayTime =
    isPlaying || currentTime > 0
      ? formatDuration(currentTime)
      : formatDuration(duration);

  return (
    <Container>
      <audio ref={audioRef} src={url} preload="metadata">
        <track kind="captions" />
      </audio>
      <PlayerRow>
        <PlayButton
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <Pause /> : <PlayArrow />}
        </PlayButton>
        <WaveformContainer onClick={handleSeek}>
          {bars.map((height, idx) => {
            const barProgress = (idx + 0.5) / BARS_COUNT;
            return (
              <Bar
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                heightRatio={height}
                played={barProgress <= progress}
              />
            );
          })}
        </WaveformContainer>
        <Duration>{displayTime}</Duration>
      </PlayerRow>
    </Container>
  );
};

export default AudioPlayer;
